import type { CustomerValueCriterionScoringBand, CustomerValueRecordStatus } from "./criterionScoring";

export type CustomerValueScoringVersionStatus = CustomerValueRecordStatus | "mixed";

export type CustomerValueScoringBandComparableField =
  | "points"
  | "minValue"
  | "maxValue"
  | "minInclusive"
  | "maxInclusive"
  | "qualitativeValue"
  | "qualitativeOptionId"
  | "qualitativeLabelDe"
  | "qualitativeLabelEn"
  | "booleanValue"
  | "rank"
  | "status"
  | "active";

export interface CustomerValueScoringVersionHistoryEntry {
  criterionId: string;
  version: number;
  status: CustomerValueScoringVersionStatus;
  createdAt: string | null;
  confirmedAt: string | null;
  supersededAt: string | null;
  updatedAt: string | null;
  activeVersion: boolean;
  replacedByVersion: number | null;
  previousVersion: number | null;
  previewActive: boolean;
  bandCount: number;
  bands: readonly CustomerValueCriterionScoringBand[];
}

export interface CustomerValueScoringVersionHistory {
  criterionId: string | null;
  entries: readonly CustomerValueScoringVersionHistoryEntry[];
  activeVersion: CustomerValueScoringVersionHistoryEntry | null;
  warnings: readonly "multiple_active_versions"[];
}

export interface CustomerValueScoringBandDiffChange {
  before: CustomerValueCriterionScoringBand;
  after: CustomerValueCriterionScoringBand;
  changedFields: readonly CustomerValueScoringBandComparableField[];
}

export interface CustomerValueScoringBandVersionDiff {
  added: readonly CustomerValueCriterionScoringBand[];
  removed: readonly CustomerValueCriterionScoringBand[];
  changed: readonly CustomerValueScoringBandDiffChange[];
  unchanged: readonly CustomerValueCriterionScoringBand[];
}

export interface CustomerValueScoringVersionSummary {
  title: string;
  version: number;
  status: CustomerValueScoringVersionStatus;
  activeVersion: boolean;
  previewActive: boolean;
  createdAt: string | null;
  confirmedAt: string | null;
  replacedByVersion: number | null;
  previousVersion: number | null;
  bandCount: number;
}

const COMPARABLE_FIELDS: readonly CustomerValueScoringBandComparableField[] = [
  "points",
  "minValue",
  "maxValue",
  "minInclusive",
  "maxInclusive",
  "qualitativeValue",
  "qualitativeOptionId",
  "qualitativeLabelDe",
  "qualitativeLabelEn",
  "booleanValue",
  "rank",
  "status",
  "active"
];

function earliestTimestamp(values: readonly (string | undefined)[]): string | null {
  const timestamps = values.filter((value): value is string => Boolean(value));
  if (timestamps.length === 0) return null;
  return timestamps.slice().sort()[0] ?? null;
}

function latestTimestamp(values: readonly (string | undefined)[]): string | null {
  const timestamps = values.filter((value): value is string => Boolean(value));
  if (timestamps.length === 0) return null;
  return timestamps.slice().sort().at(-1) ?? null;
}

function versionStatus(bands: readonly CustomerValueCriterionScoringBand[]): CustomerValueScoringVersionStatus {
  const statuses = new Set(bands.map((band) => band.status));
  return statuses.size === 1 ? [...statuses][0] ?? "archived" : "mixed";
}

function bandSortKey(band: CustomerValueCriterionScoringBand): string {
  return [
    band.rank,
    band.points ?? "none",
    band.minValue ?? "open",
    band.maxValue ?? "open",
    band.qualitativeOptionId ?? band.qualitativeValue ?? "none",
    band.booleanValue === null || band.booleanValue === undefined ? "none" : String(band.booleanValue)
  ].join(":");
}

function sortBands(bands: readonly CustomerValueCriterionScoringBand[]): CustomerValueCriterionScoringBand[] {
  return bands.slice().sort((left, right) => bandSortKey(left).localeCompare(bandSortKey(right)));
}

function candidateKeys(band: CustomerValueCriterionScoringBand): readonly string[] {
  const rangeKey = `range:${band.minValue ?? "open"}:${band.minInclusive}:${band.maxValue ?? "open"}:${band.maxInclusive}`;
  const keys = [
    `id:${band.id}`,
    `rank:${band.rank}`,
    rangeKey,
    `points:${band.points ?? "none"}`
  ];
  const optionValue = band.qualitativeOptionId ?? band.qualitativeValue;
  if (optionValue) keys.push(`option:${optionValue}`);
  if (band.booleanValue !== null && band.booleanValue !== undefined) keys.push(`boolean:${String(band.booleanValue)}`);
  return keys;
}

function changedFields(before: CustomerValueCriterionScoringBand, after: CustomerValueCriterionScoringBand): CustomerValueScoringBandComparableField[] {
  return COMPARABLE_FIELDS.filter((field) => before[field] !== after[field]);
}

function findMatchingBand(
  band: CustomerValueCriterionScoringBand,
  candidates: readonly CustomerValueCriterionScoringBand[],
  usedCandidateIds: Set<string>
): CustomerValueCriterionScoringBand | undefined {
  for (const key of candidateKeys(band)) {
    const match = candidates.find((candidate) => !usedCandidateIds.has(candidate.id) && candidateKeys(candidate).includes(key));
    if (match) return match;
  }
  return undefined;
}

export function diffCustomerValueScoringVersions(
  beforeBands: readonly CustomerValueCriterionScoringBand[],
  afterBands: readonly CustomerValueCriterionScoringBand[]
): CustomerValueScoringBandVersionDiff {
  const added: CustomerValueCriterionScoringBand[] = [];
  const removed: CustomerValueCriterionScoringBand[] = [];
  const changed: CustomerValueScoringBandDiffChange[] = [];
  const unchanged: CustomerValueCriterionScoringBand[] = [];
  const sortedBefore = sortBands(beforeBands);
  const sortedAfter = sortBands(afterBands);
  const usedAfterIds = new Set<string>();

  for (const before of sortedBefore) {
    const after = findMatchingBand(before, sortedAfter, usedAfterIds);
    if (!after) {
      removed.push(before);
      continue;
    }
    usedAfterIds.add(after.id);
    const fields = changedFields(before, after);
    if (fields.length > 0) changed.push({ before, after, changedFields: fields });
    else unchanged.push(before);
  }

  for (const after of sortedAfter) {
    if (!usedAfterIds.has(after.id)) added.push(after);
  }

  return { added, removed, changed, unchanged };
}

export function buildCustomerValueScoringVersionHistory(
  bands: readonly CustomerValueCriterionScoringBand[]
): CustomerValueScoringVersionHistory {
  const criterionIds = Array.from(new Set(bands.map((band) => band.criterionId)));
  const criterionId = criterionIds.length === 1 ? criterionIds[0] ?? null : null;
  const grouped = new Map<number, CustomerValueCriterionScoringBand[]>();
  for (const band of bands) {
    grouped.set(band.version, [...(grouped.get(band.version) ?? []), band]);
  }
  const versionsAscending = Array.from(grouped.keys()).sort((left, right) => left - right);
  const activeVersionNumber = versionsAscending
    .filter((version) => (grouped.get(version) ?? []).some((band) => band.active && band.status === "confirmed"))
    .at(-1) ?? null;
  const confirmedVersions = versionsAscending.filter((version) => (grouped.get(version) ?? []).some((band) => band.status === "confirmed"));
  const warnings = confirmedVersions.length > 1 ? ["multiple_active_versions" as const] : [];

  const entriesAscending = versionsAscending.map((version, index): CustomerValueScoringVersionHistoryEntry => {
    const versionBands = sortBands(grouped.get(version) ?? []);
    const status = versionStatus(versionBands);
    const createdAt = earliestTimestamp(versionBands.map((band) => band.createdAt));
    const updatedAt = latestTimestamp(versionBands.map((band) => band.updatedAt));
    const replacedByVersion = status === "superseded"
      ? versionsAscending.find((candidate) => candidate > version && (grouped.get(candidate) ?? []).some((band) => band.status === "confirmed")) ?? null
      : null;
    const activeVersion = activeVersionNumber === version && status === "confirmed";
    return {
      criterionId: criterionId ?? versionBands[0]?.criterionId ?? "",
      version,
      status,
      createdAt,
      confirmedAt: status === "confirmed" || status === "superseded" ? createdAt : null,
      supersededAt: status === "superseded" ? updatedAt : null,
      updatedAt,
      activeVersion,
      replacedByVersion,
      previousVersion: versionsAscending[index - 1] ?? null,
      previewActive: activeVersion,
      bandCount: versionBands.filter((band) => band.active).length,
      bands: versionBands
    };
  });
  const entries = entriesAscending.slice().sort((left, right) => right.version - left.version);
  return {
    criterionId,
    entries,
    activeVersion: entries.find((entry) => entry.activeVersion) ?? null,
    warnings
  };
}

export function buildCustomerValueScoringVersionSummaries(
  history: CustomerValueScoringVersionHistory
): CustomerValueScoringVersionSummary[] {
  return history.entries.map((entry) => ({
    title: `Version ${entry.version}`,
    version: entry.version,
    status: entry.status,
    activeVersion: entry.activeVersion,
    previewActive: entry.previewActive,
    createdAt: entry.createdAt,
    confirmedAt: entry.confirmedAt,
    replacedByVersion: entry.replacedByVersion,
    previousVersion: entry.previousVersion,
    bandCount: entry.bandCount
  }));
}
